import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Responsive, useResponsive } from '@common-fe/common-fe';
import { createGlobalStyle } from 'styled-components';

import ROUTES from '@/common/routes';
import { DefaultContentWrapper } from '@/components/wrappers/DefaultContentWrapper/DefaultContentWrapper';
import { useIsAddressUndeliverable } from '@/modules/core/components/BadAddressBanner';
import { useHorizontalMenuMode } from '@/modules/core/hooks';
import { useShowLockBanner } from '@/modules/core/hooks';
import useAllowNotifications from '@/modules/core/hooks/useAllowNotifications';
import useEmployerTerminationBanner from '@/modules/core/hooks/useEmployerTerminationBanner';
import NotificationsCenter from '@/modules/notification/NotificationsCenter';
import { useShowLimitedAccessTopBanner } from '@/modules/user/components/LimitedAccess/hooks';
import { useAuthStore } from '@/modules/user/stores';
import { matchPatternURL } from '@/utils/handlers';

import HorizontalMenu from '../Menu/HorizontalMenu';
import MobileMenu from '../Menu/MobileMenu';
import Sidebar from '../Menu/Sidebar';
import Topbar from '../Topbar';
import { Breadcrumbs } from '../Topbar/Breadcrumbs/Breadcrumbs';

import { Content } from './DashboardWrapper.styles';
import { useApplyFixedScrollStore } from './stores';

interface BodyProps {
  isFixedScroll?: boolean;
}
const BodyContext = createGlobalStyle<BodyProps>`
  body {

    overflow-y: ${({ isFixedScroll }) => (isFixedScroll ? 'scroll !important' : '')};

  }
`;
interface Props {
  children: React.ReactNode;
}

const MINIFIED_PAGES = [
  ROUTES.REIMBURSE_ME,
  ROUTES.USE_MY_MONEY,
  ROUTES.REQUEST_BILL_PAY,
  ROUTES.RECEIPT_VAULT,
  ROUTES.START_INVESTING,
  ROUTES.BUY_SELL,
  ROUTES.SELF_DIRECTED(' '),
  ROUTES.SELF_DIRECTED_INVEST_MORE(' '),
  ROUTES.SELF_DIRECTED_SWITCH_SELECT(' '),

  ROUTES.SELF_DIRECTED_EMPTY_SWITCH_QUESTIONNAIRE(' '),
  ROUTES.SELF_DIRECTED_EMPTY_SWITCH_SELECT(' '),
  ROUTES.SELF_DIRECTED_EMPTY_SWITCH_ALLOCATION(' '),

  ROUTES.MANAGED_SWITCH_EMPTY_QUESTIONNAIRE(' '),
  ROUTES.MANAGED_SWITCH_EMPTY_FUNDING_AMOUNT(' '),
  ROUTES.MANAGED_SWITCH_EMPTY_ALLOCATION(' '),

  ROUTES.MANAGED_SWITCH_QUESTIONNAIRE(' '),
  ROUTES.MANAGED_SWITCH_FUNDING_AMOUNT(' '),
  ROUTES.MANAGED_SWITCH_ALLOCATION(' '),
  ROUTES.SELF_DIRECTED_SWITCH_ALLOCATION(' '),
  ROUTES.SELF_DIRECTED_ALLOCATION(' '),
  ROUTES.MANAGED_SELL_INVESTMENTS(' '),
  ROUTES.SELF_DIRECTED_QUESTIONNAIRE(' '),
  ROUTES.INVESTMENTS_UPDATE_RISK_PORTFOLIO,
];

const WITHOUT_WRAPPER_PAGES = [
  ROUTES.SIGN_UP_ORPHAN,
  ROUTES.DOWNLOAD_FILE_BY_QR_LINK,
  ROUTES.ONE_TIME_LINKS,
  ROUTES.SIGN_UP,
  ROUTES.RESET_PASSWORD,
];
const MOBILE_FULLSCREEN_PAGES = [
  ROUTES.EXPENSES_TRANSACTION_DETAILS_CLAIM(),
  ROUTES.INVESTMENTS,
  ROUTES.MANAGE_PROFILE,
  ROUTES.SELF_DIRECTED_SELL_INVESTMENTS(),
  ROUTES.MANAGED_SELL_INVESTMENTS(),
  ROUTES.BUY_SELL,
  ROUTES.USE_MY_MONEY_SUMMARY,
  ROUTES.USE_MY_MONEY_SETUP_FIELDS,
  ROUTES.SELF_DIRECTED_INVEST_MORE(),
  ROUTES.MY_DOCUMENTS_LMN_VIEW_MOBILE(),
  ROUTES.MY_DOCUMENTS_LMN_EDIT_MOBILE(),
];

const DashboardWrapper: React.FC<Props> = ({ children }) => {
  const [minified, setMinified] = useState(false);
  const isHorizontalMenu = useHorizontalMenuMode();
  const isLockBannerShowed = useShowLockBanner();
  const isLimitedAccessTopBanner = useShowLimitedAccessTopBanner();
  const { isUndeliverable: isBadAddressBannerShowed } = useIsAddressUndeliverable();

  const { bannerHeight } = useEmployerTerminationBanner();

  const { auth, inited, user } = useAuthStore();
  const isLoading = !inited;
  const { pathname } = useLocation();
  useEffect(() => {
    const newValue = !!MINIFIED_PAGES.find((item) => pathname.includes(item.trim()));
    if (minified !== newValue) {
      setMinified(newValue);
    }
  }, [pathname, minified]);
  const isNotificationsAllowed = useAllowNotifications();
  const isNotificationsShowed = useMemo(
    () => !!user?.employee?.id && isNotificationsAllowed,
    [user, isNotificationsAllowed]
  );

  const isWithWrapper = useMemo(
    () =>
      !WITHOUT_WRAPPER_PAGES.some((template) => {
        return pathname.includes(template);
      }),
    [pathname]
  );
  const isFixedScrolled = useApplyFixedScrollStore((state) => state.isApplied);
  const { isMobile } = useResponsive();

  const isMobileFullScreen = useMemo(() => {
    if (isMobile) {
      const isFound = MOBILE_FULLSCREEN_PAGES.some((item) => matchPatternURL(item, pathname));
      return isFound;
    }
    return false;
  }, [isMobile, pathname]);

  if (isLoading) {
    return null;
  }

  if (auth && isWithWrapper) {
    return (
      <Box
        direction={isHorizontalMenu || isMobile ? 'column' : 'row'}
        data-testid="dashboard-wrapper"
        id="modal-portal-id"
        margin={{
          top:
            isLockBannerShowed || isLimitedAccessTopBanner || isBadAddressBannerShowed
              ? '62px'
              : bannerHeight || '0',
        }}
      >
        <BodyContext isFixedScroll={isFixedScrolled} />
        <div>
          <Responsive sizes={[Responsive.Tablet, Responsive.Desktop]}>
            {isHorizontalMenu ? <HorizontalMenu /> : <Sidebar minified={minified} />}
          </Responsive>
        </div>
        {!isMobileFullScreen && (
          <Responsive sizes={[Responsive.Mobile]}>
            <MobileMenu>
              <Topbar isMobileMode />
            </MobileMenu>
          </Responsive>
        )}
        <Box fill background={isMobile ? 'navigationBackground' : ''}>
          <Content
            id="page-content-root"
            style={{ position: 'relative' }}
            isMobileMode={isMobile}
            isFlatMode={isMobileFullScreen}
            isMinified={minified}
            align="center"
          >
            <DefaultContentWrapper>
              <Responsive sizes={[Responsive.Tablet, Responsive.Desktop]}>
                {!minified && <Topbar />}
              </Responsive>

              <Responsive sizes={[Responsive.Mobile]}>
                <Box pad={{ horizontal: 'spacing16', top: isMobileFullScreen ? 'spacing24' : undefined }}>
                  <Breadcrumbs />
                </Box>
              </Responsive>

              {isNotificationsShowed && <NotificationsCenter />}
              {children}
            </DefaultContentWrapper>
          </Content>
        </Box>
      </Box>
    );
  }
  return <>{children}</>;
};
export default DashboardWrapper;
